/**
 * Settings → Cleaning & changeover: the company's activities and the plant's
 * times, the product list for per-product times, and saving either.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import lineActivitiesApi, { LineActivityTime, LineActivityType, SaveLineActivityTime } from "../../../../services/line-activities-api";
import { Product, productsApi } from "../../../../services/products-api";
import { indexTimes, timeKey } from "../utils/line-activities";

export function useLineActivities(locationId: number) {
	const [types, setTypes] = useState<LineActivityType[]>([]);
	const [times, setTimes] = useState<LineActivityTime[]>([]);
	const [products, setProducts] = useState<Product[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!locationId) return undefined;
		let cancelled = false;
		setLoading(true);
		setError(null);
		Promise.all([lineActivitiesApi.get(locationId), productsApi.getAll()])
			.then(([data, catalog]) => {
				if (cancelled) return;
				setTypes(data.types);
				setTimes(data.times);
				// Activities are products too (for the plan); they are not what a time is "for".
				setProducts(catalog.filter((p) => !String(p.itemNumber ?? "").startsWith("ACT-")).sort((a, b) => a.name.localeCompare(b.name)));
			})
			.catch((err) => {
				console.error("Cleaning & changeover load failed:", err);
				if (!cancelled) setError("Could not load the cleaning and changeover times.");
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [locationId]);

	const activeTypes = useMemo(() => types.filter((t) => t.isActive), [types]);
	const index = useMemo(() => indexTimes(times), [times]);
	const productNames = useMemo(() => new Map(products.map((p) => [p.id, p.name])), [products]);

	const saveType = useCallback((type: LineActivityType) => {
		setError(null);
		lineActivitiesApi
			.saveType({ id: type.id || undefined, name: type.name, kind: type.kind, description: type.description, isActive: type.isActive })
			.then((saved) => setTypes((list) => (list.some((t) => t.id === saved.id) ? list.map((t) => (t.id === saved.id ? saved : t)) : [...list, saved])))
			.catch(() => setError("That activity was not saved — is the name already on the list?"));
	}, []);
	const addType = useCallback(() => {
		const name = `New activity ${types.length + 1}`;
		saveType({ id: 0, code: "", name, kind: "cleaning", description: "", sortOrder: types.length, isActive: true });
	}, [types.length, saveType]);

	const dismissError = useCallback(() => setError(null), []);
	const saveTime = useCallback((time: SaveLineActivityTime) => {
		setError(null);
		const key = timeKey(time.activityTypeId, time.workstation, time.productId);
		lineActivitiesApi
			.saveTime(time)
			.then((saved) =>
				setTimes((list) => {
					const rest = list.filter((t) => timeKey(t.activityTypeId, t.workstation, t.productId) !== key);
					return saved ? [...rest, saved] : rest;
				})
			)
			.catch(() => setError("That time was not saved."));
	}, []);


	return {
		types,
		activeTypes,
		times,
		index,
		products,
		productNames,
		loading,
		error,
		saveType,
		addType,
		saveTime,
		dismissError,
	};
}

export type LineActivities = ReturnType<typeof useLineActivities>;
